import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Contrast, Type, Globe, X, Sparkles, Check } from 'lucide-react';

export const AccessibilityToolbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [dyslexicFont, setDyslexicFont] = useState(false);

  const toggleHighContrast = () => {
    const next = !highContrast;
    setHighContrast(next);
    document.body.classList.toggle('high-contrast', next);
  };

  const toggleDyslexicFont = () => {
    const next = !dyslexicFont;
    setDyslexicFont(next);
    document.body.classList.toggle('font-opendyslexic', next);
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  return (
    <div className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40">
      
      {/* Floating Popover Drawer */}
      {isOpen && (
        <div className="absolute bottom-14 right-0 w-72 bg-slate-900/95 text-white p-4 rounded-3xl border border-slate-700 shadow-2xl backdrop-blur-xl space-y-4 animate-fade-in">
          <div className="flex items-center justify-between border-b border-slate-800 pb-2">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-black uppercase tracking-wider text-white">
                Accessibility & Language
              </span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Visual Accommodations */}
          <div className="space-y-2">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Visual Accommodations
            </div>

            <button
              onClick={toggleHighContrast}
              className={`w-full p-2.5 rounded-xl border text-xs font-extrabold transition flex items-center justify-between ${
                highContrast ? 'bg-amber-500 text-slate-950 border-amber-400 shadow' : 'bg-slate-800 text-slate-200 border-slate-700 hover:bg-slate-750'
              }`}
            >
              <div className="flex items-center gap-2">
                <Contrast className="w-4 h-4" />
                <span>High Contrast Mode</span>
              </div>
              {highContrast && <Check className="w-4 h-4 text-slate-950" />}
            </button>

            <button
              onClick={toggleDyslexicFont}
              className={`w-full p-2.5 rounded-xl border text-xs font-extrabold transition flex items-center justify-between ${
                dyslexicFont ? 'bg-blue-600 text-white border-blue-500 shadow' : 'bg-slate-800 text-slate-200 border-slate-700 hover:bg-slate-750'
              }`}
            >
              <div className="flex items-center gap-2">
                <Type className="w-4 h-4" />
                <span>OpenDyslexic Font</span>
              </div>
              {dyslexicFont && <Check className="w-4 h-4 text-white" />}
            </button>
          </div>

          {/* Multilingual Selector */}
          <div className="space-y-2 pt-2 border-t border-slate-800">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              System Language
            </div>

            <div className="grid grid-cols-3 gap-1.5">
              <button
                onClick={() => changeLanguage('en')}
                className={`py-2 rounded-xl text-xs font-bold transition border ${
                  i18n.language === 'en' ? 'bg-blue-600 text-white border-blue-400' : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-750'
                }`}
              >
                English
              </button>

              <button
                onClick={() => changeLanguage('hi')}
                className={`py-2 rounded-xl text-xs font-bold transition border ${
                  i18n.language === 'hi' ? 'bg-blue-600 text-white border-blue-400' : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-750'
                }`}
              >
                हिंदी
              </button>

              <button
                onClick={() => changeLanguage('mr')}
                className={`py-2 rounded-xl text-xs font-bold transition border ${
                  i18n.language === 'mr' ? 'bg-blue-600 text-white border-blue-400' : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-750'
                }`}
              >
                मराठी
              </button>
            </div>
          </div>

        </div>
      )}

      {/* Floating Trigger Button (Compact 44px Circular Pill) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        title="Accessibility & Language Settings"
        className={`w-12 h-12 rounded-full shadow-2xl flex items-center justify-center border transition-all ${
          isOpen 
            ? 'bg-amber-400 text-slate-950 border-amber-300 scale-105 ring-4 ring-amber-400/30' 
            : 'bg-slate-900/90 text-white border-slate-700 hover:bg-slate-800 hover:scale-105'
        }`}
      >
        <Globe className="w-5 h-5 text-amber-400" />
      </button>

    </div>
  );
};
